import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { Button, Card, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import { Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';
import { faCheck, faImage } from '@fortawesome/pro-light-svg-icons';
import { ConfiguratorPresetDto, ConfiguratorPresetGroupDto } from '@/services/configurator';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { useOptionConfigurator } from '../../OptionConfiguratorContext';
import { useImageUrls } from '../../useImageUrls';
import { buildConfiguratorUrl } from '../../utils/buildConfiguratorUrl';
import { useConfiguratorMutations } from '../../ConfiguratorMutationContext';
import styles from './RoomStyleCard.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface RoomStyleCardProps {
    style: ConfiguratorPresetDto;
    styleGroup: ConfiguratorPresetGroupDto;
}

export const RoomStyleCard: React.FC<RoomStyleCardProps> = ({ style, styleGroup }) => {
    const { roomId } = useParams<{ roomId: string }>();
    const { baseUrl, selectedDeadlineId, viewOnly, isShowroom } = useOptionConfigurator();
    const { getOptionPresetImageUrl } = useImageUrls();
    const { pickStyle, unpickStyle, isPickingStyle, isUnpickingStyle } = useConfiguratorMutations();

    const handleSelect = () => {
        pickStyle(style.id, roomId ? Number(roomId) : undefined);
    };

    const handleUnselect = () => {
        unpickStyle(styleGroup.id, roomId ? Number(roomId) : undefined);
    };

    const detailsSegment = roomId
        ? `room/${roomId}/style-group/${styleGroup.id}/style/${style.id}`
        : `style-group/${styleGroup.id}/style/${style.id}`;
    const detailsUrl = buildConfiguratorUrl({ baseUrl, deadlineId: selectedDeadlineId, segment: detailsSegment });

    const src = useMemo(
        () => getOptionPresetImageUrl(style.images[0]?.id ?? null),
        [style.images, getOptionPresetImageUrl]
    );
    const isSelected = useMemo(() => style.isPicked, [style]);

    return (
        <Card className={`${styles.card} ${isSelected ? styles.selected : ''}`}>
            <div className={styles.thumbnail}>
                <Thumbnail
                    src={src}
                    fallback={
                        <div className={styles.imageFallback}>
                            <FontAwesomeIcon icon={faImage} size="lg" />
                        </div>
                    }
                />
            </div>
            <div className={styles.content}>
                <div className={styles.header}>
                    <h5>{style.name}</h5>
                    <BuyerCurrencyDisplay value={style.price} />
                </div>
                <div className={styles.actions}>
                    <Button variant="tertiary" size="xs" as={Link} to={detailsUrl}>
                        <Trans>More details</Trans>
                    </Button>
                    <SelectButton
                        isSelected={isSelected}
                        forceSelection={styleGroup.forceSelection}
                        onSelect={handleSelect}
                        onUnselect={handleUnselect}
                        isPending={isPickingStyle || isUnpickingStyle}
                        viewOnly={viewOnly}
                        // Locked = options sent or deadline expired; irrelevant in the anonymous
                        // showroom, so don't block style switching there.
                        isLocked={styleGroup.isLocked && !isShowroom}
                    />
                </div>
            </div>
        </Card>
    );
};

interface SelectButtonProps {
    isSelected: boolean;
    forceSelection: boolean;
    onSelect: () => void;
    onUnselect: () => void;
    isPending: boolean;
    viewOnly: boolean;
    /** The style group has been signed/locked; the server rejects pick/unpick. */
    isLocked: boolean;
}

const SelectButton: React.FC<SelectButtonProps> = ({
    isSelected,
    forceSelection,
    onSelect,
    onUnselect,
    isPending,
    viewOnly,
    isLocked,
}) => {
    if (isLocked) {
        // Signed/locked: the server rejects pick/unpick. Show the committed state
        // for the picked style and a disabled Select for the rest — never an active button.
        return (
            <SimpleTooltip content={<Trans>This style group is locked</Trans>}>
                {isSelected ? (
                    <Button variant="tertiary" size="xs" disabled icon={faCheck} iconPosition="right">
                        <Trans>Selected</Trans>
                    </Button>
                ) : (
                    <Button variant="primary" size="xs" disabled>
                        <Trans>Select</Trans>
                    </Button>
                )}
            </SimpleTooltip>
        );
    }

    if (!isSelected) {
        return (
            <SimpleTooltip disabled={!viewOnly} content={<Trans>Selections are disabled in this view</Trans>}>
                <Button variant="primary" size="xs" onClick={onSelect} loading={isPending} disabled={viewOnly}>
                    <Trans>Select</Trans>
                </Button>
            </SimpleTooltip>
        );
    }

    if (forceSelection) {
        return (
            <Button variant="tertiary" size="xs" disabled icon={faCheck} iconPosition="right">
                <Trans>Selected</Trans>
            </Button>
        );
    }

    return (
        <SimpleTooltip disabled={!viewOnly} content={<Trans>Selections are disabled in this view</Trans>}>
            <Button variant="tertiary" size="xs" onClick={onUnselect} loading={isPending} disabled={viewOnly}>
                <Trans>Unselect</Trans>
            </Button>
        </SimpleTooltip>
    );
};
